import React, { useEffect, useRef, useState } from "react";
import { FilePdf, Image as ImageIcon, Trash, DownloadSimple, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SPORTS = [
  "general", "football", "basketball", "cricket", "tennis", "swimming",
  "athletics", "hockey", "rugby", "badminton", "volleyball", "boxing",
  "kabaddi", "table_tennis",
];

export default function SportsPrograms() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // program to preview
  const [form, setForm] = useState({ name: "", sport: "general", description: "", tags: "" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/sports-coach/programs")
      .then(({ data }) => setRows(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async () => {
    if (!file) { toast.error("Please pick a PDF or image"); return; }
    if (!form.name.trim()) { toast.error("Program name is required"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", form.name.trim());
    fd.append("sport", form.sport);
    fd.append("description", form.description || "");
    fd.append("tags", form.tags || "");
    setUploading(true);
    try {
      await api.post("/sports-coach/programs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Program uploaded");
      setShowForm(false);
      setForm({ name: "", sport: "general", description: "", tags: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeProgram = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    await api.delete(`/sports-coach/programs/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="sc-programs">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="label-tiny text-slate-500">// PROGRAM UPLOADER</div>
          <div className="text-sm text-slate-600">Upload PDF or scanned-image training plans. Assigned athletes will be able to view them.</div>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs label-tiny tracking-wider px-4 py-2"
          data-testid="sc-programs-new-btn"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "CANCEL" : "NEW PROGRAM"}
        </button>
      </div>

      {showForm && (
        <div className="border border-slate-200 bg-white p-4 mb-6 space-y-3" data-testid="sc-programs-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-tiny block mb-1 text-slate-600">PROGRAM NAME</label>
              <input
                className="w-full border border-slate-300 px-3 py-2 text-sm bg-white"
                value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Pre-season Speed Block"
                data-testid="sc-program-name"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1 text-slate-600">SPORT</label>
              <select
                className="w-full border border-slate-300 px-3 py-2 text-sm bg-white"
                value={form.sport} onChange={(e) => setForm((f) => ({ ...f, sport: e.target.value }))}
                data-testid="sc-program-sport"
              >
                {SPORTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-tiny block mb-1 text-slate-600">DESCRIPTION</label>
            <textarea
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white"
              rows={2}
              value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="What's the goal of this block?"
              data-testid="sc-program-description"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1 text-slate-600">TAGS (comma separated)</label>
            <input
              className="w-full border border-slate-300 px-3 py-2 text-sm bg-white"
              value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="pre-season, speed, block-1"
              data-testid="sc-program-tags"
            />
          </div>
          <div>
            <label className="label-tiny block mb-1 text-slate-600">FILE (PDF or scanned image · max 25MB)</label>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm"
              data-testid="sc-program-file"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={uploading}
              className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-2"
              data-testid="sc-program-submit"
            >
              {uploading ? "UPLOADING…" : "UPLOAD"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading programs…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="sc-programs-empty">
          <FilePdf size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No programs uploaded yet. Upload your first PDF plan.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((p) => (
            <div key={p.program_id} className="border border-slate-200 bg-white p-4 flex flex-col gap-2" data-testid={`sc-program-card-${p.program_id}`}>
              <div className="flex items-center gap-2">
                {p.kind === "pdf" ? <FilePdf size={20} className="text-red-500" /> : <ImageIcon size={20} className="text-cyan-600" />}
                <div className="font-heading text-sm truncate flex-1">{p.name}</div>
              </div>
              <div className="text-[10px] tracking-widest text-slate-500">{(p.sport || "general").replace(/_/g, " ").toUpperCase()}</div>
              {p.description && <div className="text-xs text-slate-600 line-clamp-2">{p.description}</div>}
              {p.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {p.tags.slice(0, 4).map((t) => (
                    <span key={t} className="text-[10px] bg-cyan-50 text-cyan-700 px-1.5 py-0.5 border border-cyan-200">#{t}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                <button
                  onClick={() => setPreview(p)}
                  className="text-cyan-600 hover:text-cyan-700 text-xs label-tiny flex items-center gap-1"
                  data-testid={`sc-program-view-${p.program_id}`}
                >
                  <DownloadSimple size={12} /> VIEW
                </button>
                <button
                  onClick={() => removeProgram(p.program_id)}
                  className="text-red-500 hover:text-red-700 text-xs"
                  data-testid={`sc-program-delete-${p.program_id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPreview(null)} data-testid="sc-program-preview">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-slate-200">
              <div className="font-heading">{preview.name}</div>
              <button onClick={() => setPreview(null)} data-testid="sc-program-preview-close"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100">
              {preview.kind === "pdf" ? (
                <iframe src={`${BACKEND_URL}${preview.url}`} title={preview.name} className="w-full h-[80vh]" />
              ) : (
                <img src={`${BACKEND_URL}${preview.url}`} alt={preview.name} className="max-w-full mx-auto" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
